import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

export interface QueryState<T> {
  data: T | undefined
  error: Error | null
  loading: boolean
  /** Re-run the query. `silent` keeps current data on screen without a loading state. */
  reload: (silent?: boolean) => Promise<void>
  setData: (fn: T | ((prev: T | undefined) => T)) => void
}

/**
 * Minimal data hook: runs `fn` whenever `deps` change, ignores stale responses,
 * and optionally refreshes on an interval. No caching, by design.
 */
export function useQuery<T>(fn: () => Promise<T>, deps: unknown[], opts: { refreshMs?: number; enabled?: boolean } = {}): QueryState<T> {
  const [data, setDataState] = useState<T | undefined>(undefined)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(opts.enabled !== false)
  const seq = useRef(0)
  const fnRef = useRef(fn)
  fnRef.current = fn

  const run = useCallback(async (silent = false) => {
    const my = ++seq.current
    if (!silent) setLoading(true)
    try {
      const d = await fnRef.current()
      if (my === seq.current) { setDataState(d); setError(null) }
    } catch (e) {
      if (my === seq.current) setError(e instanceof Error ? e : new Error(String(e)))
    } finally {
      if (my === seq.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (opts.enabled === false) { setLoading(false); return }
    setDataState(undefined)
    void run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    if (!opts.refreshMs || opts.enabled === false) return
    const t = setInterval(() => { if (document.visibilityState !== 'hidden') void run(true) }, opts.refreshMs)
    return () => clearInterval(t)
  }, [opts.refreshMs, opts.enabled, run])

  const setData = useCallback((v: T | ((prev: T | undefined) => T)) => {
    setDataState((prev) => (typeof v === 'function' ? (v as (p: T | undefined) => T)(prev) : v))
  }, [])

  return { data, error, loading, reload: run, setData }
}

/** Wraps an async action with a busy flag and error capture. */
export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>, opts: { onError?: (e: Error) => void } = {}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const fnRef = useRef(fn)
  fnRef.current = fn
  const onErr = useRef(opts.onError)
  onErr.current = opts.onError
  const run = useCallback(async (...args: A): Promise<R | undefined> => {
    setBusy(true); setError(null)
    try {
      return await fnRef.current(...args)
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e))
      setError(err)
      onErr.current?.(err)
      return undefined
    } finally {
      setBusy(false)
    }
  }, [])
  return { run, busy, error }
}

export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

/** Re-renders every `ms` so relative times ("due 47m") stay fresh. */
export function useNow(ms = 30_000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(t) }, [ms])
  return now
}

/** Tracks an element's width (for SVG charts that should fill their card). */
export function useWidth<T extends HTMLElement>(fallback = 600): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null)
  const [w, setW] = useState(fallback)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    setW(el.clientWidth || fallback)
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width) || fallback))
    ro.observe(el)
    return () => ro.disconnect()
  }, [fallback])
  return [ref, w]
}
